/**
 * The desk's keys: `←` Później, `→` Gotowe, `↑ ↓` pick another card, `↵` the card's primary,
 * `Z` cofnij, `⇧←` the term menu.
 *
 * At the DOCUMENT rather than on the desk, for the reason `plan-builder/lib/usePlanShortcuts` gives:
 * a decision is about the card, not about whatever currently holds focus, and a handler on a
 * container only fires while focus is inside it — which is most of the time and therefore the worst
 * kind of unreliable.
 *
 * ── Where it is inert ───────────────────────────────────────────────────────────────────────
 * `model/deskKeys` holds the rule and the reasons: a key is the focused widget's only when that
 * widget itself uses THAT key. This file does one job for it — reading the event's target back into
 * a `FocusKind` — and takes the verdict.
 *
 * The classification is by ROLE and by tag, never by "is it focusable": the previous version asked
 * one question for all six keys and, against the real app, that turned the keyboard off after any
 * mouse click on a radio or a row.
 *
 * `event.repeat` is DROPPED rather than honoured. Holding `→` down is not "clear the queue
 * quickly"; it is fourteen writes the trainer did not read, and the undo only reaches the last one.
 * The builder honours a held Cmd+Z for the opposite reason — walking back through a session is what
 * that key is for.
 *
 * It dispatches; it decides nothing. Whether there is a card, whether its primary is refused and
 * whether a decision is already held are all the screen's, which is where the reasons live.
 *
 * ── The handlers are held in a REF, and the listener attaches once ───────────────────────────
 * Every one of them closes over the deck, so they are fresh objects on every answer, every arrow
 * and every background read. Depending on them directly meant tearing the document listener down
 * and building it again on each of those — a `removeEventListener`/`addEventListener` pair per
 * keystroke for a handler set that had not changed in any way the document cares about. The ref is
 * written in an effect and read inside the listener, which is the pattern `useHeldResponses`
 * already uses for the responder, and the listener now depends on `enabled` alone.
 */

import { useEffect, useRef } from 'react';

import { isTextEntry } from '~/shared/lib/isTextEntry';
import { OWNS_ARROWS_ATTRIBUTE } from '~/shared/lib/ownsArrows';

import { deskKeyIsOurs, type DeskKey, type FocusKind } from '../model/deskKeys';

export interface TaskShortcuts {
  readonly later: () => void;
  readonly done: () => void;
  readonly previous: () => void;
  readonly next: () => void;
  /** The card's own primary — "Skomentuj", "Otwórz kalendarz", whatever this reason's act is. */
  readonly primary: () => void;
  readonly undo: () => void;
  /** `⇧←`: open the term menu instead of writing the default one. */
  readonly openTerms: () => void;
  /** Off while the desk has no card — the empty state and the end state swallow nothing. */
  readonly enabled: boolean;
}

/** Anything modal owns the whole keyboard while it is up. */
// The chat panel over the desk is not modal, but its keys are the conversation's, never the deck's.
const MODAL = '[role="dialog"], [role="alertdialog"], [data-chat-dock]';

/**
 * Widgets whose own keyboard contract is the four arrows.
 *
 * The NATIVE tags are listed beside the ARIA roles, and that is not belt and braces: `closest` is a
 * CSS match, so `[role="radio"]` finds an element that spells the attribute out and never a plain
 * `<input type="radio">`, which carries the role implicitly. The scope switch is exactly that — a
 * `<fieldset>` of real radios — so a selector list without `input[type="radio"]` left the platform's
 * own `→` going to the card instead of to the next option, which the spec caught.
 *
 * The last entry is for a widget whose ROLE says nothing about its keys: a low-reading card's plot
 * is a `role="img"` tab stop whose arrows walk the readings, and it marks itself
 * (`shared/lib/ownsArrows`). A role selector there would have claimed every picture on the page.
 */
const OWNS_ARROWS = `[role="radiogroup"], [role="radio"], [role="menu"], [role="menuitem"], [role="listbox"], [role="option"], [role="slider"], [role="spinbutton"], input[type="radio"], input[type="range"], select, video, [${OWNS_ARROWS_ATTRIBUTE}]`;

/** Controls the platform activates on Enter or Space. */
const CONTROL = 'button, a[href], summary, [role="button"], [role="link"], [role="switch"]';

/**
 * The attribute the queue index stamps on each of its rows.
 *
 * A row is a `<button>`, so nothing about its markup distinguishes it from the decision buttons; it
 * is the SCREEN that knows a row is a place to stand rather than an act, and it says so here.
 */
export const INDEX_ROW_ATTRIBUTE = 'data-queue-row';

/** What the keyboard is sitting on. The order is the precedence: most specific claim first. */
function focusKindOf(target: EventTarget | null): FocusKind {
  if (isTextEntry(target)) return 'text';
  if (!(target instanceof Element)) return 'page';
  if (target.closest(MODAL) !== null) return 'modal';
  if (target.closest(`[${INDEX_ROW_ATTRIBUTE}]`) !== null) return 'indexRow';
  if (target.closest(OWNS_ARROWS) !== null) return 'arrows';
  if (target.closest(CONTROL) !== null) return 'control';
  return 'page';
}

/** The event's key as one of the six the desk claims, or `null` for everything else. */
function deskKeyOf(key: string): DeskKey | null {
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowRight':
    case 'ArrowUp':
    case 'ArrowDown':
    case 'Enter':
      return key;
    case 'z':
    case 'Z':
      return 'Z';
    default:
      return null;
  }
}

export function useTaskShortcuts(shortcuts: TaskShortcuts): void {
  const enabled = shortcuts.enabled;
  const latest = useRef(shortcuts);
  useEffect(() => {
    latest.current = shortcuts;
  });

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // A held key is a queue of decisions nobody read. See the header.
      if (event.repeat) return;
      // A widget under the document already took this key for itself. React's handlers run at the
      // root, which is below this listener, so a key a component prevented arrives here marked.
      if (event.defaultPrevented) return;
      // Every chord on this screen is a BARE key, so a modifier means the browser's or the OS's.
      // `⇧←` is the one exception and it is handled inside the `ArrowLeft` arm.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const key = deskKeyOf(event.key);
      if (key === null) return;
      // `⇧→` is nobody's; `⇧←` is the term menu and is taken in the `ArrowLeft` arm.
      if (event.shiftKey && key !== 'ArrowLeft') return;
      if (!deskKeyIsOurs(focusKindOf(event.target), key)) return;

      switch (key) {
        case 'ArrowLeft':
          if (event.shiftKey) latest.current.openTerms();
          else latest.current.later();
          break;
        case 'ArrowRight':
          latest.current.done();
          break;
        case 'ArrowUp':
          latest.current.previous();
          break;
        case 'ArrowDown':
          latest.current.next();
          break;
        case 'Enter':
          latest.current.primary();
          break;
        case 'Z':
          latest.current.undo();
          break;
      }

      // Only once we know the key was ours: every arm above either acted or returned, and none of
      // these keys has a default worth keeping on this screen.
      event.preventDefault();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled]);
}
